import { Button, Grid } from '@material-ui/core';
import {
  EntityApiDefinitionCard,
  EntityConsumedApisCard,
  EntityConsumingComponentsCard,
  EntityHasApisCard,
  EntityProvidedApisCard,
  EntityProvidingComponentsCard,
} from '@backstage/plugin-api-docs';
import {
  EntityAboutCard,
  EntityDependsOnComponentsCard,
  EntityDependsOnResourcesCard,
  EntityHasComponentsCard,
  EntityHasResourcesCard,
  EntityHasSubcomponentsCard,
  EntityHasSystemsCard,
  EntityLayout,
  EntityLinksCard,
  EntitySwitch,
  EntityOrphanWarning,
  EntityProcessingErrorsPanel,
  isComponentType,
  isKind,
  hasCatalogProcessingErrors,
  isOrphan,
  hasRelationWarnings,
  EntityRelationWarning,
} from '@backstage/plugin-catalog';
import {
  EntityUserProfileCard,
  EntityGroupProfileCard,
  EntityMembersListCard,
  EntityOwnershipCard,
} from '@backstage/plugin-org';
import { EntityTechdocsContent } from '@backstage/plugin-techdocs';
import { EmptyState } from '@backstage/core-components';
import {
  Direction,
  EntityCatalogGraphCard,
} from '@backstage/plugin-catalog-graph';
import {
  RELATION_API_CONSUMED_BY,
  RELATION_API_PROVIDED_BY,
  RELATION_CONSUMES_API,
  RELATION_DEPENDENCY_OF,
  RELATION_DEPENDS_ON,
  RELATION_HAS_PART,
  RELATION_PART_OF,
  RELATION_PROVIDES_API,
} from '@backstage/catalog-model';

import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { Mermaid } from 'backstage-plugin-techdocs-addon-mermaid';
import { mermaidAddonProps } from '../../techdocsMermaid';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import {
  EntityDomainScoreContent,
  EntityScoreCardContent,
} from '@internal/plugin-score-card';
import {
  EntityApiEnvironmentsDefinitionCard,
  isApiDefinitionEnvironmentsAvailable,
} from '@internal/plugin-api-docs-environments';
import {
  EntityKubernetesContent,
  isKubernetesAvailable,
} from '@backstage/plugin-kubernetes';
import {
  EntitySonarQubeCard,
  EntitySonarQubeContentPage,
  isSonarQubeAvailable,
} from '@backstage-community/plugin-sonarqube';
import { SystemAboutCard } from './SystemAboutCard';
import { AwsAccountCard, hasAwsAccountCardData } from './AwsAccountCard';
import { SystemAwsAccountsCard } from './SystemAwsAccountsCard';
import { When } from './When';
import {
  EntityPredicate,
  consumesApis,
  dependsOnComponents,
  dependsOnResources,
  hasApiConsumers,
  hasApiProviders,
  hasApis,
  hasComponents,
  hasLinks,
  hasResources,
  hasSubcomponents,
  hasSystems,
  hasTechDocs,
  providesApis,
} from './entityPredicates';

const techdocsContent = (
  <EntityTechdocsContent>
    <TechDocsAddons>
      <ReportIssue />
      <Mermaid {...mermaidAddonProps} />
    </TechDocsAddons>
  </EntityTechdocsContent>
);

const cicdContent = (
  // This is an example of how you can implement your company's logic in entity page.
  // You can for example enforce that all components of type 'service' should use GitHubActions
  <EntitySwitch>
    {/*
      Here you can add support for different CI/CD services, for example
      using @backstage-community/plugin-github-actions as follows:
      <EntitySwitch.Case if={isGithubActionsAvailable}>
        <EntityGithubActionsContent />
      </EntitySwitch.Case>
     */}

    <EntitySwitch.Case>
      <EmptyState
        title="No CI/CD available for this entity"
        missing="info"
        description="You need to add an annotation to your component if you want to enable CI/CD for it. You can read more about annotations in Backstage by clicking the button below."
        action={
          <Button
            variant="contained"
            color="primary"
            href="https://backstage.io/docs/features/software-catalog/well-known-annotations"
          >
            Read more
          </Button>
        }
      />
    </EntitySwitch.Case>
  </EntitySwitch>
);

const entityWarningContent = (
  <>
    <EntitySwitch>
      <EntitySwitch.Case if={isOrphan}>
        <Grid item xs={12}>
          <EntityOrphanWarning />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>

    <EntitySwitch>
      <EntitySwitch.Case if={hasRelationWarnings}>
        <Grid item xs={12}>
          <EntityRelationWarning />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>

    <EntitySwitch>
      <EntitySwitch.Case if={hasCatalogProcessingErrors}>
        <Grid item xs={12}>
          <EntityProcessingErrorsPanel />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>
  </>
);

const overviewContent = (
  <Grid container spacing={3} alignItems="stretch">
    {entityWarningContent}
    <Grid item md={6}>
      <EntityAboutCard />
    </Grid>
    <Grid item md={6} xs={12}>
      <EntityCatalogGraphCard height={400} />
    </Grid>

    <When is={hasLinks}>
      <Grid item md={4} xs={12}>
        <EntityLinksCard />
      </Grid>
    </When>
    <When is={hasSubcomponents}>
      <Grid item md={8} xs={12}>
        <EntityHasSubcomponentsCard />
      </Grid>
    </When>

    <When is={isSonarQubeAvailable}>
      <Grid item md={6} xs={12}>
        <EntitySonarQubeCard />
      </Grid>
    </When>
  </Grid>
);

/**
 * A tab is worth showing only when at least one card on it has content —
 * otherwise the reader has to click to discover there is nothing there.
 *
 * `providesOrConsumesApis` is deliberately NOT called `hasApis`: that name is
 * taken by the System-page predicate meaning "owns API entities via hasPart",
 * which is a different question. It reads catalog **relations** rather than
 * `spec.providesApis`/`spec.consumesApis`, because the cards on the tab resolve
 * relations too — so the guard and its contents cannot disagree.
 */
const providesOrConsumesApis: EntityPredicate = entity =>
  providesApis(entity) || consumesApis(entity);

const hasAnyDependencies: EntityPredicate = entity =>
  dependsOnComponents(entity) || dependsOnResources(entity);

// Identical on the service and website pages; shared so the two copies cannot
// drift apart.
const dependenciesContent = (
  <Grid container spacing={3} alignItems="stretch">
    <When is={dependsOnComponents}>
      <Grid item md={6}>
        <EntityDependsOnComponentsCard />
      </Grid>
    </When>
    <When is={dependsOnResources}>
      <Grid item md={6}>
        <EntityDependsOnResourcesCard />
      </Grid>
    </When>
  </Grid>
);

/**
 * Shared by the service and website API tabs, with each card guarded so an
 * entity that only provides (or only consumes) APIs does not get an empty second
 * card beside the populated one.
 */
const apiTabContent = (
  <Grid container spacing={3} alignItems="stretch">
    <When is={providesApis}>
      <Grid item md={6}>
        <EntityProvidedApisCard />
      </Grid>
    </When>
    <When is={consumesApis}>
      <Grid item md={6}>
        <EntityConsumedApisCard />
      </Grid>
    </When>
  </Grid>
);

const serviceEntityPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      {overviewContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/ci-cd" title="CI/CD">
      {cicdContent}
    </EntityLayout.Route>

    <EntityLayout.Route
      path="/kubernetes"
      title="Kubernetes"
      if={isKubernetesAvailable}
    >
      <EntityKubernetesContent />
    </EntityLayout.Route>

    <EntityLayout.Route path="/api" title="API" if={providesOrConsumesApis}>
      {apiTabContent}
    </EntityLayout.Route>

    <EntityLayout.Route
      path="/dependencies"
      title="Dependencies"
      if={hasAnyDependencies}
    >
      {dependenciesContent}
    </EntityLayout.Route>

    {/* The compact card also appears on the Overview; this tab carries the
        plugin's full dashboard rather than a second copy of that card. */}
    <EntityLayout.Route
      path="/code-quality"
      title="Code Quality"
      if={isSonarQubeAvailable}
    >
      <EntitySonarQubeContentPage />
    </EntityLayout.Route>

    <EntityLayout.Route path="/docs" title="Docs" if={hasTechDocs}>
      {techdocsContent}
    </EntityLayout.Route>
  </EntityLayout>
);

const websiteEntityPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      {overviewContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/ci-cd" title="CI/CD">
      {cicdContent}
    </EntityLayout.Route>

    <EntityLayout.Route
      path="/kubernetes"
      title="Kubernetes"
      if={isKubernetesAvailable}
    >
      <EntityKubernetesContent />
    </EntityLayout.Route>

    <EntityLayout.Route path="/api" title="API" if={providesOrConsumesApis}>
      {apiTabContent}
    </EntityLayout.Route>

    <EntityLayout.Route
      path="/dependencies"
      title="Dependencies"
      if={hasAnyDependencies}
    >
      {dependenciesContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/docs" title="Docs" if={hasTechDocs}>
      {techdocsContent}
    </EntityLayout.Route>
  </EntityLayout>
);

/**
 * NOTE: This page is designed to work on small screens such as mobile devices.
 * This is based on Material UI Grid. If breakpoints are used, each grid item must set the `xs` prop to a column size or to `true`,
 * since this does not default. If no breakpoints are used, the items will equitably share the available space.
 * https://material-ui.com/components/grid/#basic-grid.
 */

const defaultEntityPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      {overviewContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/docs" title="Docs" if={hasTechDocs}>
      {techdocsContent}
    </EntityLayout.Route>
  </EntityLayout>
);

const componentPage = (
  <EntitySwitch>
    <EntitySwitch.Case if={isComponentType('service')}>
      {serviceEntityPage}
    </EntitySwitch.Case>

    <EntitySwitch.Case if={isComponentType('website')}>
      {websiteEntityPage}
    </EntitySwitch.Case>

    <EntitySwitch.Case>{defaultEntityPage}</EntitySwitch.Case>
  </EntitySwitch>
);

const apiPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item md={6}>
          <EntityAboutCard />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityCatalogGraphCard height={400} />
        </Grid>
        <When is={hasLinks}>
          <Grid item md={4} xs={12}>
            <EntityLinksCard />
          </Grid>
        </When>
        {/* Previously nested in a `<Grid container item md={12}>` that carried no
            `spacing`, so these two cards had no gutter between them. Flattened to
            siblings of the outer container: the gutter is restored and there is no
            wrapper left to survive both cards being hidden. */}
        <When is={hasApiProviders}>
          <Grid item md={6}>
            <EntityProvidingComponentsCard />
          </Grid>
        </When>
        <When is={hasApiConsumers}>
          <Grid item md={6}>
            <EntityConsumingComponentsCard />
          </Grid>
        </When>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/definition" title="Definition">
      <Grid container spacing={3}>
        <Grid item xs={12}>
          {/* Feature 0055: an API that publishes a definition per environment
              (temabit.com/api-definition-url-template) gets a tab for each one;
              every other API keeps the single definition in the catalog. */}
          <EntitySwitch>
            <EntitySwitch.Case if={isApiDefinitionEnvironmentsAvailable}>
              <EntityApiEnvironmentsDefinitionCard />
            </EntitySwitch.Case>
            <EntitySwitch.Case>
              <EntityApiDefinitionCard />
            </EntitySwitch.Case>
          </EntitySwitch>
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

const userPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item xs={12} md={6}>
          <EntityUserProfileCard />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityOwnershipCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

const groupPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item xs={12} md={6}>
          <EntityGroupProfileCard />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityOwnershipCard />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityMembersListCard />
        </Grid>
        <When is={hasLinks}>
          <Grid item xs={12} md={6}>
            <EntityLinksCard />
          </Grid>
        </When>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

const systemPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3} alignItems="stretch">
        {entityWarningContent}
        <Grid item md={6} xs={12}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              {/* System About card carries PRM Codes as a field (feature 0015),
                  replacing the former standalone PrmCodesCard. */}
              <SystemAboutCard />
            </Grid>
            {/* Self-wrapping card: supplies its own <Grid item> and renders
                nothing (no empty grid slot) when the System has no
                aws-accounts. Placed under the About card in the left column. */}
            <SystemAwsAccountsCard />
          </Grid>
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityCatalogGraphCard height={400} />
        </Grid>
        <When is={hasLinks}>
          <Grid item md={4} xs={12}>
            <EntityLinksCard />
          </Grid>
        </When>
        <When is={hasComponents}>
          <Grid item md={8}>
            <EntityHasComponentsCard />
          </Grid>
        </When>
        <When is={hasApis}>
          <Grid item md={6}>
            <EntityHasApisCard />
          </Grid>
        </When>
        <When is={hasResources}>
          <Grid item md={6}>
            <EntityHasResourcesCard />
          </Grid>
        </When>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/score" title="Score">
      <Grid container spacing={3} alignItems="stretch">
        <Grid item xs={12}>
          <EntityScoreCardContent />
        </Grid>
      </Grid>
    </EntityLayout.Route>
    <EntityLayout.Route path="/diagram" title="Diagram">
      <EntityCatalogGraphCard
        direction={Direction.TOP_BOTTOM}
        title="System Diagram"
        height={700}
        relations={[
          RELATION_PART_OF,
          RELATION_HAS_PART,
          RELATION_API_CONSUMED_BY,
          RELATION_API_PROVIDED_BY,
          RELATION_CONSUMES_API,
          RELATION_PROVIDES_API,
          RELATION_DEPENDENCY_OF,
          RELATION_DEPENDS_ON,
        ]}
        unidirectional={false}
      />
    </EntityLayout.Route>
  </EntityLayout>
);

const resourceEntityPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3} alignItems="stretch">
        {entityWarningContent}
        <Grid item md={6} xs={12}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <EntityAboutCard />
            </Grid>
            {/* Gate on the card's OWN availability predicate, not just the
                Resource type: the card returns null for an aws-account without
                an id annotation, which would otherwise leave exactly the empty
                spaced row this guard exists to remove. */}
            <When is={hasAwsAccountCardData}>
              <Grid item xs={12}>
                <AwsAccountCard />
              </Grid>
            </When>
          </Grid>
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityCatalogGraphCard height={400} />
        </Grid>
        <When is={hasLinks}>
          <Grid item md={4} xs={12}>
            <EntityLinksCard />
          </Grid>
        </When>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

const domainPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3} alignItems="stretch">
        {entityWarningContent}
        <Grid item md={6}>
          <EntityAboutCard />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityCatalogGraphCard height={400} />
        </Grid>
        <When is={hasSystems}>
          <Grid item md={6}>
            <EntityHasSystemsCard />
          </Grid>
        </When>
      </Grid>
    </EntityLayout.Route>

    {/* Feature 0058: the aggregate of the Domain's direct members — Systems,
        and one row per subdomain — the same tab a System has. */}
    <EntityLayout.Route path="/score" title="Score">
      <Grid container spacing={3} alignItems="stretch">
        <Grid item xs={12}>
          <EntityDomainScoreContent />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

export const entityPage = (
  <EntitySwitch>
    <EntitySwitch.Case if={isKind('component')} children={componentPage} />
    <EntitySwitch.Case if={isKind('api')} children={apiPage} />
    <EntitySwitch.Case if={isKind('group')} children={groupPage} />
    <EntitySwitch.Case if={isKind('user')} children={userPage} />
    <EntitySwitch.Case if={isKind('system')} children={systemPage} />
    <EntitySwitch.Case if={isKind('resource')} children={resourceEntityPage} />
    <EntitySwitch.Case if={isKind('domain')} children={domainPage} />

    <EntitySwitch.Case>{defaultEntityPage}</EntitySwitch.Case>
  </EntitySwitch>
);
